import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RestoreIcon from "@mui/icons-material/Restore";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { layoutTokens, typographyTokens } from "@/shared/theme";
import { useBoardNavigation } from "./use-board-navigation";

export const BoardMoveControls = ({
  label,
  position,
  count,
  onNavigate,
  onRestore,
  onTogglePlay,
  playing = false,
  onSettings,
  disabled = false,
}: {
  label: string;
  position: number;
  count: number;
  onNavigate: (position: number) => void;
  onRestore?: () => void;
  onTogglePlay?: () => void;
  playing?: boolean;
  onSettings?: () => void;
  disabled?: boolean;
}) => {
  const navigate = (next: number) => {
    if (!disabled && next >= 0 && next <= count && next !== position) onNavigate(next);
  };
  useBoardNavigation((direction) => navigate(position + direction), !disabled);
  const action = (name: string, icon: React.ReactNode, onClick: () => void, unavailable = false) => (
    <Tooltip title={name}>
      <span>
        <IconButton aria-label={name} disabled={unavailable} onClick={onClick}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
  return (
    <Box
      role="group"
      aria-label="Move navigation"
      sx={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        minWidth: 0,
        minHeight: layoutTokens.controlHeight,
        borderBlock: layoutTokens.dividerBorder,
        borderColor: "divider",
        bgcolor: "background.paper",
        "& .MuiIconButton-root": {
          width: layoutTokens.controlHeight,
          height: layoutTokens.controlHeight,
          borderRadius: 0,
          color: "primary.main",
          "&:hover": { bgcolor: "secondary.main", color: "secondary.contrastText" },
        },
      }}
    >
      {action("First position", <FirstPageIcon />, () => navigate(0), disabled || position <= 0)}
      {action("Previous move", <NavigateBeforeIcon />, () => navigate(position - 1), disabled || position <= 0)}
      <Typography
        noWrap
        title={label}
        sx={{
          flex: 1,
          minWidth: 0,
          px: 0,
          textAlign: "center",
          fontSize: "caption.fontSize",
          fontWeight: "fontWeightBold",
          letterSpacing: typographyTokens.tracking.label,
        }}
      >
        {label}
      </Typography>
      {onTogglePlay &&
        action(
          playing ? "Pause verified line" : "Play verified line",
          playing ? <PauseIcon /> : <PlayArrowIcon />,
          onTogglePlay,
          disabled,
        )}
      {action("Next move", <NavigateNextIcon />, () => navigate(position + 1), disabled || position >= count)}
      {action("Last move", <LastPageIcon />, () => navigate(count), disabled || position >= count)}
      {onRestore && action("Restore position", <RestoreIcon />, onRestore)}
      {onSettings && action("Board settings", <SettingsOutlinedIcon />, onSettings)}
    </Box>
  );
};
